/*
 * FROSTBEULE – chrome scripts (owner: CHROME). Loaded with defer by
 * sections/frost-header.liquid and sections/frost-announcement-bar.liquid.
 *   <fb-header>        sticky / transparent state, sliding hover pill, desktop dropdowns,
 *                      cart bubble pop, --fb-header-height on <html>
 *   <fb-menu-drawer>   mobile menu (focus trap, Esc, overlay click, aria-expanded, snowfall)
 *   <fb-announcement>  rotating messages (timed by a CSS hairline) or ticker, pause button (WCAG 2.2.2)
 * Custom elements survive shopify:section:load / unload without extra code.
 */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;

  function C() {
    return window.FROSTBEULE || {};
  }

  function motion() {
    var c = C();
    return typeof c.motion === 'function' ? c.motion() : root.getAttribute('data-motion') || 'full';
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, wait || 150);
    };
  }

  function lockScroll() {
    if (C().lockScroll) C().lockScroll();
    else root.classList.add('fb-scroll-locked');
  }

  function unlockScroll() {
    if (C().unlockScroll) C().unlockScroll();
    else root.classList.remove('fb-scroll-locked');
  }

  function focusTrap(el, opts) {
    if (C().focusTrap) return C().focusTrap(el, opts);
    /* minimal fallback (frost-global.js missing): Esc + Tab cycling */
    var previous = doc.activeElement;
    var onKey = function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (opts && opts.onEscape) opts.onEscape(e);
        return;
      }
      if (e.key !== 'Tab') return;
      var nodes = el.querySelectorAll('a[href], button:not([disabled]), summary, input:not([type="hidden"])');
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (e.shiftKey && doc.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && doc.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    doc.addEventListener('keydown', onKey, true);
    var initial = opts && typeof opts.initialFocus === 'string' ? el.querySelector(opts.initialFocus) : null;
    (initial || el).focus();
    return {
      release: function (returnFocus) {
        doc.removeEventListener('keydown', onKey, true);
        if (returnFocus !== false && previous && previous.focus) previous.focus();
      },
    };
  }

  /* ======================================================================
     <fb-header>
     ====================================================================== */
  var headerConnects = 0;

  if (!customElements.get('fb-header')) {
    customElements.define(
      'fb-header',
      class FbHeader extends HTMLElement {
        connectedCallback() {
          headerConnects += 1;
          this.section = this.closest('.shopify-section') || this.parentElement;
          this.bar = this.querySelector('.fb-header__bar');
          this.overlay = this.classList.contains('fb-header--overlay');
          this.sticky = this.classList.contains('fb-header--sticky');

          if (this.section) {
            this.section.classList.toggle('fb-header-section--sticky', this.sticky);
            this.section.classList.toggle('fb-header-section--overlay', this.overlay);
          }
          root.classList.toggle('fb-has-overlay-header', this.overlay);

          this.initScrollState();
          this.initHeight();
          this.initPill();
          this.initDropdowns();
          this.initCartBubble(headerConnects > 1);
        }

        disconnectedCallback() {
          if (this.io) this.io.disconnect();
          if (this.ro) this.ro.disconnect();
          if (this.mo) this.mo.disconnect();
          if (this.sentinel && this.sentinel.parentNode) this.sentinel.parentNode.removeChild(this.sentinel);
          if (this.onResize) window.removeEventListener('resize', this.onResize);
          if (this.onDocClick) doc.removeEventListener('click', this.onDocClick);
          if (this.onDocKey) doc.removeEventListener('keydown', this.onDocKey);
          if (this.pillRaf) window.cancelAnimationFrame(this.pillRaf);
          root.classList.remove('fb-has-overlay-header');
        }

        /* Solid state once the page is scrolled: an absolutely positioned sentinel at the top of
           the document (no scroll listener). Height = distance to the bar + a little travel. */
        initScrollState() {
          var self = this;
          if (!('IntersectionObserver' in window)) {
            this.classList.add('is-scrolled');
            return;
          }
          this.sentinel = doc.createElement('span');
          this.sentinel.setAttribute('aria-hidden', 'true');
          this.sentinel.className = 'fb-header-sentinel';
          this.sentinel.style.cssText =
            'position:absolute;top:0;left:0;width:1px;pointer-events:none;visibility:hidden;';
          doc.body.appendChild(this.sentinel);
          this.placeSentinel();

          this.io = new IntersectionObserver(function (entries) {
            var entry = entries[entries.length - 1];
            self.classList.toggle('is-scrolled', !entry.isIntersecting);
          });
          this.io.observe(this.sentinel);

          this.onResize = debounce(function () {
            self.placeSentinel();
          }, 150);
          window.addEventListener('resize', this.onResize, { passive: true });
        }

        placeSentinel() {
          if (!this.sentinel) return;
          /* natural (un-stuck) offset of the header = height of the header-group sections above it */
          var top = 0;
          var el = this.section ? this.section.previousElementSibling : null;
          while (el) {
            if (el.classList && el.classList.contains('shopify-section')) top += el.offsetHeight;
            el = el.previousElementSibling;
          }
          var travel = this.overlay ? 24 : 4;
          this.sentinel.style.height = Math.max(1, Math.round(top + travel)) + 'px';
        }

        initHeight() {
          var self = this;
          var set = function () {
            if (!self.bar) return;
            root.style.setProperty('--fb-header-height', self.bar.offsetHeight + 'px');
          };
          set();
          if ('ResizeObserver' in window && this.bar) {
            this.ro = new ResizeObserver(function () {
              set();
              self.placeSentinel();
            });
            this.ro.observe(this.bar);
            /* sections above the header (announcement bar) change the sticky threshold */
            var el = this.section ? this.section.previousElementSibling : null;
            while (el) {
              if (el.classList && el.classList.contains('shopify-section')) this.ro.observe(el);
              el = el.previousElementSibling;
            }
          }
        }

        /* Sliding hover pill: one element behind the links, clipped to the hovered / focused link.
           Appears in place (snap) when coming from outside, then glides between links. */
        initPill() {
          var self = this;
          var nav = this.querySelector('.fb-header__nav');
          var pill = this.querySelector('.fb-nav__pill');
          if (!nav || !pill) return;
          this.nav = nav;
          this.pill = pill;

          var linkOf = function (target) {
            var link = target && target.closest ? target.closest('.fb-nav__link') : null;
            return link && nav.contains(link) ? link : null;
          };

          nav.addEventListener('pointerover', function (e) {
            if (e.pointerType && e.pointerType !== 'mouse') return;
            var link = linkOf(e.target);
            if (link) self.movePill(link);
          });
          nav.addEventListener('pointerleave', function () {
            self.hidePill();
          });
          nav.addEventListener('focusin', function (e) {
            var link = linkOf(e.target);
            var visible = false;
            try {
              visible = link && link.matches(':focus-visible');
            } catch (err) {
              visible = !!link;
            }
            if (visible) self.movePill(link);
          });
          nav.addEventListener('focusout', function (e) {
            if (!e.relatedTarget || !nav.contains(e.relatedTarget)) self.hidePill();
          });
        }

        movePill(link) {
          var pill = this.pill;
          var navRect = this.nav.getBoundingClientRect();
          var r = link.getBoundingClientRect();
          var left = Math.max(0, r.left - navRect.left);
          var right = Math.max(0, navRect.right - r.right);
          var snap = !pill.classList.contains('is-visible') || motion() !== 'full';
          if (snap) pill.classList.add('is-snapping');
          pill.style.setProperty('--pill-l', left.toFixed(1) + 'px');
          pill.style.setProperty('--pill-r', right.toFixed(1) + 'px');
          pill.classList.add('is-visible');
          if (snap) {
            if (this.pillRaf) window.cancelAnimationFrame(this.pillRaf);
            this.pillRaf = window.requestAnimationFrame(function () {
              pill.classList.remove('is-snapping');
            });
          }
        }

        hidePill() {
          if (this.pill) this.pill.classList.remove('is-visible');
        }

        /* Desktop dropdowns (<details>): one open at a time, outside click / Esc / focus leave closes */
        initDropdowns() {
          var self = this;
          this.details = Array.prototype.slice.call(this.querySelectorAll('[data-fb-nav-details]'));
          if (!this.details.length) return;

          this.details.forEach(function (d) {
            d.addEventListener('toggle', function () {
              if (!d.open) return;
              self.details.forEach(function (other) {
                if (other !== d) other.open = false;
              });
            });
            d.addEventListener('focusout', function (e) {
              if (e.relatedTarget && !d.contains(e.relatedTarget)) d.open = false;
            });
          });

          this.onDocClick = function (e) {
            self.details.forEach(function (d) {
              if (d.open && !d.contains(e.target)) d.open = false;
            });
          };
          this.onDocKey = function (e) {
            if (e.key !== 'Escape' && e.key !== 'Esc') return;
            self.details.forEach(function (d) {
              if (!d.open) return;
              d.open = false;
              var summary = d.querySelector('summary');
              if (summary && d.contains(doc.activeElement)) summary.focus();
            });
          };
          doc.addEventListener('click', this.onDocClick);
          doc.addEventListener('keydown', this.onDocKey);
        }

        /* Cart bubble: Dawn's cart scripts replace #cart-icon-bubble's innerHTML after every cart
           change → when the count changed, the bubble pops and the bag jiggles. In the theme editor a
           re-rendered header loses cart-drawer.js' click binding, so it is restored here. */
        initCartBubble(isReRender) {
          var bubble = this.querySelector('#cart-icon-bubble');
          if (!bubble) return;
          var readCount = function () {
            var el = bubble.querySelector('.cart-count-bubble [aria-hidden="true"]');
            return el ? el.textContent.trim() : bubble.querySelector('.cart-count-bubble') ? '99+' : '0';
          };
          var last = readCount();

          if ('MutationObserver' in window) {
            this.mo = new MutationObserver(function () {
              var now = readCount();
              if (now === last) return;
              last = now;
              var count = bubble.querySelector('.cart-count-bubble');
              if (!count || motion() === 'none') return;
              [count, bubble].forEach(function (el) {
                el.classList.remove('is-bumped');
              });
              void bubble.offsetWidth;
              count.classList.add('is-bumped');
              bubble.classList.add('is-bumped');
              window.setTimeout(function () {
                count.classList.remove('is-bumped');
                bubble.classList.remove('is-bumped');
              }, 700);
            });
            this.mo.observe(bubble, { childList: true, subtree: true, characterData: true });
          }

          if (isReRender && !bubble.hasAttribute('data-fb-bound')) {
            var drawer = doc.querySelector('cart-drawer');
            if (!drawer || typeof drawer.open !== 'function') return;
            bubble.setAttribute('data-fb-bound', '');
            bubble.setAttribute('role', 'button');
            bubble.setAttribute('aria-haspopup', 'dialog');
            bubble.addEventListener('click', function (e) {
              e.preventDefault();
              drawer.open(bubble);
            });
            bubble.addEventListener('keydown', function (e) {
              if (e.code && e.code.toUpperCase() === 'SPACE') {
                e.preventDefault();
                drawer.open(bubble);
              }
            });
          }
        }
      }
    );
  }

  /* ======================================================================
     <fb-menu-drawer>
     ====================================================================== */
  if (!customElements.get('fb-menu-drawer')) {
    customElements.define(
      'fb-menu-drawer',
      class FbMenuDrawer extends HTMLElement {
        connectedCallback() {
          var self = this;
          this.panel = this.querySelector('.fb-drawer__panel');
          this.isOpen = false;
          this.toggles = Array.prototype.slice.call(doc.querySelectorAll('[aria-controls="' + this.id + '"]'));

          this.onToggleClick = function (e) {
            e.preventDefault();
            if (self.isOpen) self.close();
            else self.open();
          };
          this.toggles.forEach(function (t) {
            t.addEventListener('click', self.onToggleClick);
          });

          this.onClick = function (e) {
            if (e.target.closest('[data-fb-drawer-close]')) {
              self.close();
              return;
            }
            var link = e.target.closest('a[href]');
            if (link && self.contains(link)) self.close(false);
          };
          this.addEventListener('click', this.onClick);

          this.mq = window.matchMedia('(min-width: 990px)');
          this.onMq = function (e) {
            if (e.matches && self.isOpen) self.close(false);
          };
          if (this.mq.addEventListener) this.mq.addEventListener('change', this.onMq);
          else if (this.mq.addListener) this.mq.addListener(this.onMq);
        }

        disconnectedCallback() {
          var self = this;
          if (this.isOpen) {
            this.isOpen = false;
            unlockScroll();
            if (this.trap) this.trap.release(false);
          }
          clearTimeout(this.lowerTimer);
          this.toggles.forEach(function (t) {
            t.removeEventListener('click', self.onToggleClick);
          });
          if (this.mq) {
            if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.onMq);
            else if (this.mq.removeListener) this.mq.removeListener(this.onMq);
          }
          if (this.snow && typeof this.snow.destroy === 'function') this.snow.destroy();
          this.snow = null;
        }

        setExpanded(value) {
          this.toggles.forEach(function (t) {
            t.setAttribute('aria-expanded', value ? 'true' : 'false');
          });
        }

        /* light snowfall in the navy panel – shared engine from frost-global.js (FBSnow), once */
        mountSnow() {
          if (this.snow || motion() === 'none') return;
          var host = this.querySelector('[data-fb-drawer-snow]');
          var snow = window.FBSnow;
          if (!host || !snow || typeof snow.mount !== 'function') return;
          try {
            this.snow = snow.mount(host, { density: 22, wind: 0.15, color: '#ffffff' }) || true;
          } catch (e) {
            this.snow = true;
          }
        }

        open() {
          var self = this;
          if (this.isOpen) return;
          this.isOpen = true;
          clearTimeout(this.lowerTimer);
          var section = this.closest('.shopify-section');
          if (section) section.classList.add('fb-header-section--drawer-open');
          this.classList.add('is-open');
          this.setExpanded(true);
          lockScroll();
          this.mountSnow();
          this.trap = focusTrap(this.panel, {
            initialFocus: '.fb-drawer__close',
            onEscape: function () {
              self.close();
            },
          });
        }

        close(returnFocus) {
          if (!this.isOpen) return;
          this.isOpen = false;
          this.classList.remove('is-open');
          this.setExpanded(false);
          unlockScroll();
          if (this.trap) this.trap.release(returnFocus !== false);
          this.trap = null;
          var section = this.closest('.shopify-section');
          var delay = motion() === 'none' ? 0 : 320;
          this.lowerTimer = setTimeout(function () {
            if (section) section.classList.remove('fb-header-section--drawer-open');
          }, delay);
        }
      }
    );
  }

  /* ======================================================================
     <fb-announcement>
     ====================================================================== */
  if (!customElements.get('fb-announcement')) {
    customElements.define(
      'fb-announcement',
      class FbAnnouncement extends HTMLElement {
        connectedCallback() {
          var self = this;
          this.bar = this.closest('.fb-ann') || this;
          this.mode = this.getAttribute('data-mode') || 'rotate';
          this.speed = Math.max(5, parseFloat(this.getAttribute('data-speed')) || 40);
          this.toggle = this.querySelector('[data-fb-ann-toggle]');
          this.toggleLabel = this.querySelector('[data-fb-ann-toggle-label]');
          this.userPaused = false;
          this.editorPaused = false;

          if (this.toggle) {
            this.onToggle = function () {
              self.userPaused = !self.userPaused;
              self.applyPause();
            };
            this.toggle.addEventListener('click', this.onToggle);
          }

          this.onMotion = function () {
            if (self.mode === 'marquee') self.measure();
            else self.restart();
          };
          doc.addEventListener('frost:motion-change', this.onMotion);

          this.onBlockSelect = function (e) {
            if (!self.contains(e.target)) return;
            self.editorPaused = true;
            if (self.mode === 'rotate' && self.slides) {
              var i = self.slides.indexOf(e.target);
              if (i > -1) self.go(i);
            }
            self.applyPause();
          };
          this.onBlockDeselect = function (e) {
            if (!self.contains(e.target)) return;
            self.editorPaused = false;
            self.applyPause();
          };
          doc.addEventListener('shopify:block:select', this.onBlockSelect);
          doc.addEventListener('shopify:block:deselect', this.onBlockDeselect);

          if (this.mode === 'rotate') this.initRotate();
          else this.initMarquee();
          this.applyPause();
        }

        disconnectedCallback() {
          if (this.ro) this.ro.disconnect();
          if (this.readyRaf) window.cancelAnimationFrame(this.readyRaf);
          doc.removeEventListener('frost:motion-change', this.onMotion);
          doc.removeEventListener('shopify:block:select', this.onBlockSelect);
          doc.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
        }

        isPaused() {
          return this.userPaused || this.editorPaused;
        }

        applyPause() {
          var paused = this.isPaused();
          this.bar.classList.toggle('is-paused', paused);
          if (this.toggle) this.toggle.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
          if (this.toggleLabel) {
            this.toggleLabel.textContent = this.getAttribute(paused ? 'data-label-play' : 'data-label-pause') || '';
          }
        }

        /* Rotate ------------------------------------------------------------------------
           The hairline's CSS animation is the timer: animationend → next slide. Pausing the
           line (button, hover, keyboard focus, editor) pauses the rotation with it. */
        initRotate() {
          var self = this;
          this.slides = Array.prototype.slice.call(this.querySelectorAll('.fb-ann__slide'));
          this.progress = this.querySelector('.fb-ann__progress-bar');
          this.index = 0;
          if (this.slides.length < 2 || !this.progress) return;

          this.progress.addEventListener('animationend', function () {
            self.go(self.index + 1);
            self.restart();
          });

          /* first frame without transitions (the first slide is already in place) */
          this.readyRaf = window.requestAnimationFrame(function () {
            self.readyRaf = window.requestAnimationFrame(function () {
              self.bar.classList.add('is-ready');
              self.restart();
            });
          });
        }

        restart() {
          if (!this.progress) return;
          this.progress.classList.remove('is-running');
          if (motion() === 'none' || !this.slides || this.slides.length < 2) return;
          void this.progress.offsetWidth;
          this.progress.classList.add('is-running');
        }

        go(next) {
          if (!this.slides || !this.slides.length) return;
          var len = this.slides.length;
          next = ((next % len) + len) % len;
          if (next === this.index) return;
          var prev = this.slides[this.index];
          var current = this.slides[next];
          this.slides.forEach(function (s) {
            s.classList.remove('is-leaving');
          });
          prev.classList.remove('is-active');
          prev.classList.add('is-leaving');
          current.classList.add('is-active');
          this.index = next;
          clearTimeout(this.leaveTimer);
          this.leaveTimer = setTimeout(function () {
            prev.classList.remove('is-leaving');
          }, 620);
        }

        /* Marquee ------------------------------------------------------------------ */
        initMarquee() {
          var self = this;
          this.viewport = this.querySelector('.fb-ann__viewport');
          this.track = this.querySelector('.fb-ann__track');
          this.group = this.querySelector('.fb-ann__group:not(.fb-ann__group--clone)');
          this.template = this.querySelector('.fb-ann__group--clone');
          if (!this.viewport || !this.track || !this.group || !this.template) return;

          var schedule = debounce(function () {
            self.measure();
          }, 120);
          if ('ResizeObserver' in window) {
            this.ro = new ResizeObserver(schedule);
            this.ro.observe(this.viewport);
            this.ro.observe(this.group);
          } else {
            window.addEventListener('resize', schedule, { passive: true });
          }
          if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(schedule);
          this.measure();
        }

        measure() {
          if (!this.group || !this.viewport) return;
          if (motion() === 'none') {
            this.bar.classList.remove('is-running');
            return;
          }
          var groupWidth = this.group.getBoundingClientRect().width;
          var viewWidth = this.viewport.clientWidth;
          if (!groupWidth || !viewWidth) return;

          /* clones needed so the track still covers the viewport after shifting one group */
          var needed = Math.max(1, Math.ceil(viewWidth / groupWidth));
          var count = this.track.querySelectorAll('.fb-ann__group--clone').length;
          while (count < needed) {
            this.track.appendChild(this.template.cloneNode(true));
            count += 1;
          }
          while (count > needed) {
            this.track.removeChild(this.track.lastElementChild);
            count -= 1;
          }

          var shift = -Math.round(groupWidth * 100) / 100;
          var duration = Math.max(6, groupWidth / this.speed);
          this.style.setProperty('--fb-ann-shift', shift + 'px');
          this.style.setProperty('--fb-ann-duration', duration.toFixed(2) + 's');
          this.bar.classList.add('is-running');
        }
      }
    );
  }
})();
